/* global document, DNUKScriptLoader, syndicationSetupAndLogin */
import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import loadScript from 'simple-load-script';
import { GoogleFont } from 'react-typography';
import { StaticQuery, graphql } from 'gatsby';
import { withTheme } from 'emotion-theming';
import {
  Header,
  Footer,
  SiteSections,
  SessionSetter
} from 'm3.intl.ui-styleguide-v2';
import Skeleton from 'react-loading-skeleton';

import './index.css';
import '../../data/twitterFetcher_min';

import 'm3.intl.ui-styleguide-v2/src/styles/fonts.scss';
import 'm3.intl.ui-styleguide/lib/m3-ui-styleguide.css';
import 'm3.intl.ui-styleguide-v2/dist/m3.intl.ui-styleguide-v2.esm.css';

const query = graphql`
  query LayoutQuery {
    dnukReflectiveNotesCss: file(name: { eq: "dnuk_reflectivenotes" }) {
      publicURL
    }
    jqueryFancyBox: file(name: { eq: "jquery_fancyBox" }) {
      publicURL
    }
  }
`;

export const LayoutContext = React.createContext();

class TemplateWrapper extends React.Component {
  constructor() {
    super();
    this.state = {
      config: {},
      fancyBoxLoaded: false
    };
  }

  componentDidMount() {
    const { eRepId } = this.props;
    this.getConfig()
      .then(config => {
        this.setState({ config });
        if (
          config &&
          config.dnuk_host &&
          eRepId &&
          config.isM3medical === false
        ) {
          this.handleDNUKTracking(eRepId);
        }
      })
      .catch(() => console.log('Config failed to load'));

    loadScript('/api/script/userback', { inBody: true })
      .then(() => {})
      .catch(err => console.log(err));
  }

  handleDNUKTracking = erepid => {
    const SYNDICATION_ID = 'dnuk';

    // DNUKScriptLoader.loadAPIModule('login', 'edetail', 'profile', 'navigation', 'forum', 'site','learningrecord', () => {
    syndicationSetupAndLogin(SYNDICATION_ID, (dnuk, user) => {
      dnuk.eDetail.logInteraction(erepid);
    });
  };

  getConfig = () =>
    fetch('/api/config', {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then(json => {
        this.setState({ config: json });
        return json;
      })
      .catch(() => {
        console.error('Failed to get config');
        return { hideMoreHighlightsFromNavigation: false };
      });

  setStateInParent = newState => {
    this.setState(newState);
  };

  render() {
    const { config } = this.state;
    const { children, googleFontsUsed } = this.props;
    const isProduction = config && config.isProduction;
    const userTypeId = config && config.userTypeId;

    const childrenWithProps = React.Children.map(children, child =>
      React.cloneElement(child, {
        ...this.props,
        loadScript,
        config
      })
    );

    return (
      <LayoutContext.Provider value={this.state}>
        <div className="layout">
          <GoogleFont typography={googleFontsUsed} />
          <Helmet
            title="Conference Highlights - Doctors.net.uk"
            meta={[
              { name: 'description', content: 'Conference' },
              { name: 'keywords', content: 'conference, highlights' },
              { name: 'theme-color', content: '#eee' }
            ]}>
            <link
              rel="stylesheet"
              type="text/css"
              href="https://cdnjs.cloudflare.com/ajax/libs/fancybox/2.1.5/jquery.fancybox.min.css"
            />
            <link
              rel="stylesheet"
              type="text/css"
              href={`${config.dnuk_host}/_resources/css/dnuk.forum/dnuk.forum.embedded.css`}
            />
            <link
              rel="stylesheet"
              type="text/css"
              href={`${config.dnuk_host}/_resources/css/dnuk.forum/dnuk.forum.inline-addons.css`}
            />
            <script
              src="https://securepubads.g.doubleclick.net/tag/js/gpt.js"
              async="true"></script>
            <script
              type="text/javascript"
              src={`https://cdn${
                isProduction ? '' : '-integration'
              }.eu.m3.com/promotions/prebid.js`}
              async="true"></script>
            <script
              type="text/javascript"
              src={`https://cdn${
                isProduction ? '' : '-integration'
              }.eu.m3.com/promotions/dnuk_ads.min.js`}
              defer="true"
              onLoad="(function() { 
                const script = document.createElement('script'); 
                script.innerHTML = `
                  (function() { 
                    var dnukAdConfig = { 
                      area: 'conferencehighlights', 
                      useComponents: false,
                    }; 
                    if (typeof window.AdScriptInit === 'function') { 
                        window.AdScriptInit(dnukAdConfig); 
                      } 
                    })();
                  `;
                script.id = 'dnuk-execute-ad-script';
                document.body.appendChild(script);
                })();"></script>
          </Helmet>
          <ScriptLoaderWrapper setStateInParent={this.setStateInParent} />
          {isProduction !== undefined && userTypeId !== undefined ? (
            <SessionSetter isProduction={isProduction}>
              <div id="m3-header">
                <div
                  className="leaderboard-advert"
                  id={config?.leaderboardAdId}
                  hidden></div>
                <Header
                  userTypeId={userTypeId}
                  isProduction={isProduction}
                  section={SiteSections.EDUCATION}
                  sectionUrl={`${
                    isProduction ? 'https://www' : 'https://fftest'
                  }.doctors.net.uk/education`}
                  showModal
                />
              </div>
            </SessionSetter>
          ) : (
            <Skeleton height={114} />
          )}
          {childrenWithProps}
          {isProduction !== undefined && userTypeId !== undefined ? (
            <div id="m3-footer">
              <Footer userTypeId={userTypeId} isProduction={isProduction} />
            </div>
          ) : (
            undefined
          )}
        </div>
      </LayoutContext.Provider>
    );
  }
}

const ScriptLoaderWrapper = ({ setStateInParent }) => {
  return (
    <StaticQuery
      query={query}
      render={data => {
        return (
          <>
            <Helmet>
              <link
                rel="stylesheet"
                type="text/css"
                href={`${data.dnukReflectiveNotesCss.publicURL}`}
              />
            </Helmet>
            <ScriptLoader setStateInParent={setStateInParent} data={data} />
          </>
        );
      }}
    />
  );
};

const ScriptLoader = ({ setStateInParent, data }) => {
  const { fancyBoxLoaded } = useContext(LayoutContext);
  useEffect(() => {
    if (fancyBoxLoaded === false) {
      loadScript(data.jqueryFancyBox.publicURL, {
        inBody: true
      })
        .then(() => {
          return setStateInParent({ fancyBoxLoaded: true });
        })
        .catch(err => console.log(err));
    }
  }, []);
  return <></>;
};

export default withTheme(TemplateWrapper);

TemplateWrapper.propTypes = {
  children: PropTypes.func,
  googleFontsUsed: PropTypes.any
};

ScriptLoaderWrapper.propTypes = {
  setStateInParent: PropTypes.func
};

ScriptLoader.propTypes = {
  setStateInParent: PropTypes.func,
  data: PropTypes.any
};
